import { FormikProvider, useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useErrorBoundary, useLocalize, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	FlexGap,
	GraphQLErrorMessage,
	Heading3,
	IconButton,
	Input,
	JunkIcon,
	PencilEditBoxIcon,
	UndoIcon,
	tokens,
} from '@treams/treams-ui';

const Row = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${tokens.gap.element}px;
	padding: ${tokens.padding.container}px;
	border-bottom: 1px solid ${tokens.border.subtle};

	&:last-child {
		border-bottom: 0;
	}
`;

export default function GoalDriverEdit(props: {
	driver?: graphql.GoalDriver;
	onRefresh: () => void;
}) {
	const { t } = useTranslation();
	const { language } = useLocalize();
	const { isHRBusinessPartner } = useViewerRoles();
	const formik = useFormik({
		initialValues: {
			translations: props.driver?.title ?? [
				{
					languageID: language.id,
					text: '',
				},
			],
		},
		onSubmit: () => {},
	});

	const [editing, setEditing] = useState(!props.driver && !isHRBusinessPartner);

	const isArchived = !!props.driver?.archivedAt;

	const [error, attempt, reset] = useErrorBoundary();

	const [update, { loading: loadingUpdate }] = graphql.useUpdateGoalDriverMutation();
	const [create, { loading: loadingCreate }] = graphql.useCreateGoalDriverMutation();
	const [archive, { loading: loadingArchive }] = graphql.useArchiveGoalDriverMutation();
	const [restore, { loading: loadingRestore }] = graphql.useRestoreGoalDriverMutation();

	const loading = loadingUpdate || loadingCreate || loadingArchive || loadingRestore;

	const onEdit = () => {
		setEditing(true);
		reset();
	};

	const onSave = attempt(async () => {
		setEditing(false);

		const title = formik.values.translations
			.filter((translation) => translation.text.length > 0)
			.map(({ languageID, text }) => ({ languageID, text }));

		formik.values.translations = title;

		if (props.driver) {
			await update({
				variables: {
					input: {
						id: props.driver.id,
						title,
					},
				},
			});
		} else {
			await create({
				variables: {
					input: {
						title,
					},
				},
			});
		}

		props.onRefresh();
	});

	const onRestore = attempt(async () => {
		await restore({
			variables: {
				input: {
					id: props.driver!.id,
				},
			},
		});

		props.onRefresh();
	});

	const onDelete = attempt(async () => {
		await archive({
			variables: {
				input: {
					id: props.driver!.id,
				},
			},
		});

		props.onRefresh();
	});

	const title =
		formik.values.translations
			.filter((translation) => translation.languageID === language.id)
			.concat(formik.values.translations)[0]?.text ?? '';

	if (editing) {
		return (
			<Row>
				<FlexGap flexDirection="column" gap={tokens.gap.inline} minWidth={0} flexGrow={1}>
					<GraphQLErrorMessage error={error} />
					<FormikProvider value={formik}>
						<Input.Localizable name="translations" />
					</FormikProvider>
				</FlexGap>
				<Button type="primary" title={t('common.save')} onClick={onSave} loading={loading} />
			</Row>
		);
	}

	return (
		<Row>
			<FlexGap flexDirection="column" gap={tokens.gap.inline} minWidth={0} flexGrow={1}>
				<GraphQLErrorMessage error={error} />
				<Heading3 mb={0}>{title}</Heading3>
			</FlexGap>
			<FlexGap alignItems="center" gap={tokens.gap.element} flexShrink={0}>
				{!isHRBusinessPartner && !isArchived && (
					<IconButton type="secondary" icon={<PencilEditBoxIcon />} disabled={loading} onClick={onEdit} />
				)}
				{!isHRBusinessPartner &&
					props.driver &&
					(isArchived ? (
						<IconButton
							type="secondary"
							icon={<UndoIcon />}
							disabled={loading}
							onClick={onRestore}
						/>
					) : (
						<IconButton
							type="destructive"
							icon={<JunkIcon />}
							disabled={loading}
							onClick={onDelete}
						/>
					))}
			</FlexGap>
		</Row>
	);
}
